import Button from '@/global/components/Button/ButtonBase'
import { GridCellParams } from '@/global/components/DataGrid/utils/types'
import { hasPermission } from '@/hooks/useBackendMenu'
import { useStore } from '@/store'
import dayjs from 'dayjs'
import React, { lazy, memo, Suspense, useState } from 'react'
import { Registro } from 'service/api'
import { match, P } from 'ts-pattern'
import { InputLoading } from './Input'
import { useDigitacaoContext } from './pages/DigitacaoLayout'
import { entradasSaidas, isElementVisible } from './utils/helpers'

const InputDigitacao = memo(lazy(() => import('./Input')))

const RegistrosCell: React.FC<GridCellParams<Registro[]>> = ({
    row,
    value,
}) => {
    const { view } = useDigitacaoContext()
    const esconder = useStore(
        state => state.company?.esconderLinhasOndeNaoTrabalha,
    )

    const isDisabled = !hasPermission(['create', 'Registros']) || !row.isAtivo
    const [editando, setEditando] = useState(false)

    if (esconder === 'S' && row.registros.length === 0 && !editando) {
        if (row.feriado)
            return (
                <>
                    <span className="mx-6">Feriado</span>
                    <Button onClick={() => setEditando(true)} variant="link">
                        Editar
                    </Button>
                </>
            )
        if (row.trabalhaNoDia === false)
            return (
                <>
                    <span className="mx-6">Sem horário de trabalho</span>
                    <Button onClick={() => setEditando(true)} variant="link">
                        Editar
                    </Button>
                </>
            )
    }

    return (
        <div
            id={`Registros_${row.dia}`}
            onKeyDown={handleKeyDown}
            data-index={row.id}
            data-view={view}
            className="flex flex-col size-full justify-evenly items-center"
        >
            {Array.from({
                length: match<number>(row.registros.length)
                    .with(P.number.between(17, 24), () => 4)
                    .with(P.number.between(11, 16), () => 3)
                    .with(P.number.between(5, 10), () => 2)
                    .otherwise(() => 1),
            })
                .map((_, index) => index)
                .map(group => (
                    <div
                        key={`group_${group}`}
                        className="flex flex-row w-full justify-evenly items-center gap-1"
                    >
                        {entradasSaidas
                            .slice(group * 6, (group + 1) * 6)
                            .map((key, index) => {
                                const registro = value?.at(group * 6 + index)

                                return (
                                    <Suspense
                                        fallback={
                                            <InputLoading
                                                key={`loading_${row.dia}_${key}`}
                                            />
                                        }
                                    >
                                        <InputDigitacao
                                            key={`input_${row.dia}_${key}`}
                                            dia={row.dia}
                                            diaRegistro={registro?.data}
                                            entSai={key}
                                            origem={registro?.origem}
                                            status={registro?.statusAprovacao}
                                            registroId={registro?.id}
                                            defaultValue={registro?.hora}
                                            funcionarioId={row.funcionarioId}
                                            disabled={isDisabled}
                                            saidaDiaAnterior={
                                                registro?.saidaDiaAnterior ===
                                                'S'
                                            }
                                            entradaDiaSeguinte={
                                                registro?.entradaDiaSeguinte ===
                                                'S'
                                            }
                                        />
                                    </Suspense>
                                )
                            })}
                    </div>
                ))}
        </div>
    )
}

/**
 * Primeiro input vazio e editável do container (células preenchidas são
 * readOnly — nunca `disabled` — e ficam fora da navegação por Tab).
 */
function findEmptyInput(container: Element | null) {
    if (!container) return undefined
    return Array.from(
        container.querySelectorAll<HTMLInputElement>('input:not([disabled])'),
    ).find(input => !input.value && input.tabIndex !== -1)
}

function focusInput(input: HTMLInputElement) {
    if (!isElementVisible(input)) {
        input.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
    input.focus()
}

function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Tab') return
    if (event.shiftKey) event.stopPropagation()

    const isShiftTab = event.shiftKey
    const value = (event.target as HTMLInputElement).value
    // Célula preenchida: mantém Tab/Shift+Tab nativos do navegador
    if (value.length !== 0) return

    event.preventDefault()
    const index = event.currentTarget.dataset.index
    const view = event.currentTarget.dataset.view
    const [, data] = event.currentTarget.id.split('_')

    if (view === 'mensal') {
        let dia = dayjs(data)
        // Avança/retrocede dias até achar célula vazia (dias lotados são pulados)
        for (let i = 0; i < 62; i++) {
            dia = isShiftTab ? dia.subtract(1, 'day') : dia.add(1, 'day')
            const container = document.getElementById(
                `Registros_${dia.format('YYYY-MM-DD')}`,
            )
            if (!container) return

            const emptyInput = findEmptyInput(container)
            if (emptyInput) {
                focusInput(emptyInput)
                return
            }
        }
    } else {
        let nextIndex = Number(index)
        for (let i = 0; i < 62; i++) {
            nextIndex = isShiftTab ? nextIndex - 1 : nextIndex + 1
            const nextRow = document.querySelector(
                `[data-index="${nextIndex}"]`,
            )
            if (!nextRow) return

            const emptyInput = findEmptyInput(nextRow)
            if (emptyInput) {
                focusInput(emptyInput)
                return
            }
        }
    }
}

export default RegistrosCell
